import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  TrendingDown,
  Edit,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../../config/api';

export const BudgetOverview = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBudgets();
  }, [selectedYear]);

  useEffect(() => {
    if (selectedBudgetId) {
      fetchBudgetPerformance(selectedBudgetId);
    } else {
      setPerformance(null);
    }
  }, [selectedBudgetId]);

  const fetchBudgets = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/budget', {
        params: { year: selectedYear }
      });
      setBudgets(response.data || []);
      // Auto-select first budget if available
      if (response.data && response.data.length > 0 && !selectedBudgetId) {
        setSelectedBudgetId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching budgets:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load budgets';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetPerformance = async (budgetId) => {
    try {
      setError('');
      const response = await api.get(`/budget/${budgetId}/performance`);
      setPerformance(response.data);
    } catch (error) {
      console.error('Error fetching budget performance:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load budget performance';
      setError(errorMessage);
    }
  };

  const formatCurrency = (amount, symbol = '€') => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return symbol + '0.00';
    }
    return symbol + Number(amount).toFixed(2);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 60) return 'success';
    if (percentage < 80) return 'warning';
    return 'error';
  };

  const getVarianceColor = (variance) => {
    if (variance < 0) return 'success'; // Under budget (good for expenses)
    if (variance > 0) return 'error'; // Over budget (bad for expenses)
    return 'default';
  };

  const getTotalActual = () => {
    if (!performance || !performance.performance) return 0;
    return performance.performance.reduce((sum, item) => sum + item.actual_amount, 0);
  };

  const getTotalBudgeted = () => {
    if (!performance) return 0;
    return performance.total_budgeted || 0;
  };

  const getSpendingPercentage = () => {
    const budgeted = getTotalBudgeted();
    if (budgeted === 0) return 0;
    return (getTotalActual() / budgeted) * 100;
  };

  const selectedBudget = budgets.find(b => b.id === selectedBudgetId);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Budget Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setSelectedBudgetId(null);
              }}
            >
              <MenuItem value={new Date().getFullYear()}>{new Date().getFullYear()}</MenuItem>
              <MenuItem value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</MenuItem>
              <MenuItem value={new Date().getFullYear() - 2}>{new Date().getFullYear() - 2}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Budget</InputLabel>
            <Select
              value={selectedBudgetId || ''}
              label="Budget"
              onChange={(e) => setSelectedBudgetId(e.target.value)}
            >
              {budgets.map((budget) => (
                <MenuItem key={budget.id} value={budget.id}>
                  {budget.budget_name} {budget.is_approved && <CheckCircle sx={{ ml: 1, fontSize: 16 }} />}
                </MenuItem>
              ))}
              {budgets.length === 0 && (
                <MenuItem disabled>No budgets found</MenuItem>
              )}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchBudgets();
              if (selectedBudgetId) {
                fetchBudgetPerformance(selectedBudgetId);
              }
            }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Edit />}
            onClick={() => {
              setError('Budget creation form not yet implemented');
            }}
          >
            New Budget
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && !budgets.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedBudget && (
        <>
          {/* Budget Summary Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Budget
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {formatCurrency(getTotalBudgeted(), selectedBudget.currency?.symbol || '€')}
                  </Typography>
                  <LinearProgress 
                    value={getSpendingPercentage()} 
                    variant="determinate" 
                    color={getProgressColor(getSpendingPercentage())}
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {getSpendingPercentage().toFixed(1)}% spent
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Actual Spending
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {formatCurrency(getTotalActual(), selectedBudget.currency?.symbol || '€')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTotalActual() <= getTotalBudgeted() ? (
                      <>
                        <TrendingUp color="success" />
                        <Typography variant="body2" color="success.main">
                          Under budget
                        </Typography>
                      </>
                    ) : (
                      <>
                        <TrendingDown color="error" />
                        <Typography variant="body2" color="error.main">
                          Over budget
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Remaining Budget
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {formatCurrency(getTotalBudgeted() - getTotalActual(), selectedBudget.currency?.symbol || '€')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedBudget.period?.period_name || 'Current period'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Status
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={selectedBudget.is_approved ? 'Approved' : 'Draft'}
                      color={selectedBudget.is_approved ? 'success' : 'default'}
                      icon={selectedBudget.is_approved ? <CheckCircle /> : <Edit />}
                    />
                  </Box>
                  {selectedBudget.approved_at && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Approved: {format(parseISO(selectedBudget.approved_at), 'dd/MM/yyyy')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Budget by Category */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Budget Performance by Account
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell align="right">Budgeted</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell align="right">Variance</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performance && performance.performance && performance.performance.length > 0 ? (
                    performance.performance.map((item) => {
                      const percentage = item.budgeted_amount > 0 
                        ? (item.actual_amount / item.budgeted_amount) * 100 
                        : 0;
                      const isUnderBudget = item.actual_amount <= item.budgeted_amount;
                      
                      return (
                        <TableRow key={item.account_id} hover>
                          <TableCell>{item.account_code}</TableCell>
                          <TableCell>{item.account_name}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.budgeted_amount, selectedBudget.currency?.symbol || '€')}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.actual_amount, selectedBudget.currency?.symbol || '€')}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={getVarianceColor(item.variance) + '.main'}
                            >
                              {formatCurrency(item.variance, selectedBudget.currency?.symbol || '€')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ({item.variance_percent.toFixed(1)}%)
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <LinearProgress
                              value={Math.min(percentage, 100)}
                              variant="determinate"
                              color={getProgressColor(percentage)}
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isUnderBudget ? 'On Track' : 'Over Budget'}
                              color={isUnderBudget ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" sx={{ py: 4 }}>
                          {loading ? 'Loading...' : 'No budget performance data available'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {!selectedBudget && !loading && budgets.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Budgets Found
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            Create a new budget to get started
          </Typography>
          <Button variant="contained" startIcon={<Edit />}>
            Create Budget
          </Button>
        </Paper>
      )}
    </Box>
  );
};
